import React, { useEffect, useState } from "react";
import axios from "../utils/api";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Keyframes for loader
if (typeof document !== "undefined") {
  const styleId = "detailsview-loader-keyframes";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes loading-bar {
        0% { transform: translateX(-60%); }
        50% { transform: translateX(10%); }
        100% { transform: translateX(120%); }
      }
    `;
    document.head.appendChild(style);
  }
}

const styles = {
  pageWrap: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    py: { xs: 2, md: 4 },
    px: { xs: 2, sm: 3, md: 4 },
    width: "100%",
    boxSizing: "border-box",
  },
  innerWrap: {
    width: "100%",
    maxWidth: 800,
    margin: "0 auto",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },
  backButton: {
    bgcolor: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      bgcolor: "#f5f5f5",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  title: {
    fontWeight: 700,
    color: "#1a1a2e",
    fontSize: { xs: "1.25rem", md: "1.5rem" },
  },
  subtitle: {
    color: "#64748b",
    fontSize: "0.875rem",
    mt: 0.5,
  },
  card: {
    borderRadius: 3,
    overflow: "hidden",
    bgcolor: "white",
    boxShadow: "none",
    border: "1px solid rgba(0,0,0,0.06)",
    minHeight: 400,
  },
  cardContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    py: 2.5,
    px: 3,
    "&:last-child": { pb: 2.5 },
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: "1.2rem",
    color: "#1e293b",
    mb: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  metaGrid: {
    flex: 1,
    minHeight: 0,
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 2,
    mt: 2,
    pt: 2,
    borderTop: "1px solid rgba(0,0,0,0.06)",
    flexWrap: "wrap",
  },
};

function MetaRow({ label, value, valueSx }) {
  return (
    <Box sx={metaRowStyles.row}>
      <Typography sx={metaRowStyles.label}>{label}</Typography>
      <Typography sx={{ ...metaRowStyles.value, ...(valueSx || {}) }} title={value}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

const metaRowStyles = {
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 1.5,
    py: 0.75,
    borderBottom: "1px solid rgba(0,0,0,0.04)",
    "&:last-of-type": { borderBottom: "none" },
  },
  label: {
    fontSize: "0.8125rem",
    color: "#64748b",
    fontWeight: 500,
    flexShrink: 0,
  },
  value: {
    fontSize: "0.875rem",
    color: "#1e293b",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
    maxWidth: "65%",
    textAlign: "right",
  },
};

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        const response = await axios.get(`/sessions/${id}`);
        if (response.data.success) {
          setCustomerData(response.data.data);
        } else {
          setError("Failed to fetch customer data.");
        }
      } catch (err) {
        setError("An error occurred while fetching customer data.");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [id]);

  const handleConfirm = async () => {
    try {
      const response = await axios.patch("/sessions-status", {
        sessionId: id,
        status: "conformed",
      });

      if (response.data.success) {
        setCustomerData((prevData) => ({
          ...prevData,
          status: "conformed",
        }));
      }
    } catch (error) {
      console.error("Error confirming session:", error.message);
    }
  };

  const handleReject = async () => {
    try {
      const response = await axios.patch("/sessions-status", {
        sessionId: id,
        status: "rejected",
      });

      if (response.data.success) {
        setCustomerData((prevData) => ({
          ...prevData,
          status: "rejected",
        }));
      }
    } catch (error) {
      console.error("Error rejecting session:", error.message);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
          px: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 260,
            height: 260,
            borderRadius: "28px",
            bgcolor: "rgba(15,23,42,0.9)",
            boxShadow:
              "0 24px 60px rgba(15,23,42,0.55), 0 0 0 1px rgba(148,163,184,0.25)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: "-40%",
              background:
                "conic-gradient(from 180deg at 50% 50%, rgba(56,189,248,0.2), rgba(129,140,248,0.35), rgba(56,189,248,0.2))",
              opacity: 0.8,
              filter: "blur(30px)",
              animation: "spin 12s linear infinite",
            }}
          />
          <Box
            sx={{
              position: "relative",
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: "rgba(15,23,42,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 0 0 1px rgba(148,163,184,0.4), 0 0 0 12px rgba(15,23,42,0.9)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: -2,
                borderRadius: "50%",
                border: "3px solid transparent",
                borderTopColor: "#38bdf8",
                borderRightColor: "#6366f1",
                animation: "spin 1s linear infinite",
              }}
            />
            <Typography
              sx={{
                position: "relative",
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#e5e7eb",
              }}
            >
              S
            </Typography>
          </Box>
          <Box sx={{ position: "relative", textAlign: "center", px: 3 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                color: "#e5e7eb",
                mb: 0.5,
              }}
            >
              Loading session details
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
              Fetching customer and booking information…
            </Typography>
          </Box>
          <Box
            sx={{
              position: "relative",
              width: "68%",
              height: 4,
              borderRadius: 999,
              bgcolor: "rgba(15,23,42,1)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, transparent, #38bdf8, #6366f1, transparent)",
                animation: "loading-bar 1.4s ease-in-out infinite",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageWrap}>
      <Box sx={styles.innerWrap}>
        <Box sx={styles.header}>
          <IconButton
            onClick={() => navigate(-1)}
            size="medium"
            sx={styles.backButton}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={styles.title}>Customer details</Typography>
            <Typography sx={styles.subtitle}>
              Session and booking information
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!error && customerData && (
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={10} md={8}>
            <Card elevation={0} sx={styles.card}>
              <CardContent sx={styles.cardContent}>
                <Typography sx={styles.cardTitle}>
                  {customerData.name}
                </Typography>
                <Box sx={styles.metaGrid}>
                  <MetaRow label="Email" value={customerData.email} />
                  <MetaRow label="Mobile" value={customerData.mobileNumber} />
                  <MetaRow label="Company" value={customerData.companyName} />
                  <MetaRow label="Address" value={customerData.address} />
                  <MetaRow
                    label="Booking date"
                    value={
                      customerData.bookedDate
                        ? new Date(customerData.bookedDate).toLocaleDateString("en-GB")
                        : "—"
                    }
                  />
                  <MetaRow
                    label="Status"
                    value={customerData.status}
                    valueSx={{
                      color:
                        customerData.status === "conformed"
                          ? "#16a34a"
                          : customerData.status === "rejected"
                          ? "#b91c1c"
                          : "#1e293b",
                    }}
                  />
                </Box>
                <Box sx={styles.actions}>
                  {customerData.status === "pending" && (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleConfirm}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 2,
                        }}
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={handleReject}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 2,
                        }}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {(customerData.status === "conformed" || customerData.status === "rejected") && (
                    <Box sx={{ width: "100%", textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: customerData.status === "conformed" ? "#16a34a" : "#b91c1c",
                          fontWeight: 600,
                        }}
                      >
                        {customerData.status === "conformed" ? "Confirmed" : "Rejected"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        )}
      </Box>
    </Box>
  );
};

export default CustomerDetails;
